import { useEffect, useState } from 'react'
import { AlertIcon, CheckIcon } from '../components/icons'
import { sanitizeRegistration } from '../lib/format'
import { vehicleSummary } from '../lib/vehicleSummary'
import type { ResolveWidgetVehicleResponse } from '../types/PublicBookingChannelTypes'

export interface ManualVehicle {
  make: string
  model: string
}

export interface VehicleStepProps {
  /** Business name, used to make the opening screen feel like the garage's own. */
  businessName: string
  registration: string
  onRegistrationChange: (value: string) => void
  onLookup: () => void
  status: 'idle' | 'loading' | 'success' | 'error'
  vehicle: ResolveWidgetVehicleResponse | null
  error: string | null
  /** When false, the customer may continue without a confirmed lookup. */
  lookupRequired: boolean
  manualVehicle: ManualVehicle | null
  onManualVehicleChange: (value: ManualVehicle | null) => void
  /** Continue without a vehicle. Absent when the channel requires a lookup. */
  onSkip?: () => void
}

/**
 * Registration entry — the opening screen, with the plate as the hero.
 *
 * Knowing the vehicle first is what lets the next screen lead with a reason to
 * book rather than a generic list. But it must never be a wall: a failed lookup
 * falls through to manual make and model, and unless the channel requires a
 * confirmed vehicle the customer can skip it entirely and tell the garage when
 * they arrive.
 */
export function VehicleStep({
  businessName,
  registration,
  onRegistrationChange,
  onLookup,
  status,
  vehicle,
  error,
  lookupRequired,
  manualVehicle,
  onManualVehicleChange,
  onSkip,
}: VehicleStepProps) {
  const [showManualEntry, setShowManualEntry] = useState(false)

  useEffect(() => {
    if (status === 'error' && !lookupRequired) setShowManualEntry(true)
  }, [lookupRequired, status])

  const showManual = showManualEntry || manualVehicle !== null

  return (
    <div className="ac-stack">
      <div className="ac-stack ac-stack--tight">
        <p className="ac-eyebrow">Book with {businessName}</p>
        <h3 className="ac-heading ac-heading--display">What&apos;s your registration?</h3>
        <p className="ac-prose">
          We&apos;ll find your vehicle and show you what it needs.
        </p>
      </div>

      <div className="ac-field">
        <label className="ac-sr-only" htmlFor="ac-reg-input">
          Vehicle registration
        </label>
        <div className="ac-plate">
          <span className="ac-plate__country" aria-hidden="true">
            <span className="ac-plate__stars">★</span>
            GB
          </span>
          <input
            id="ac-reg-input"
            type="text"
            className="ac-plate__input"
            placeholder="AB12 CDE"
            value={registration}
            onChange={(event) => onRegistrationChange(sanitizeRegistration(event.target.value))}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                onLookup()
              }
            }}
            maxLength={8}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            enterKeyHint="search"
            aria-invalid={status === 'error'}
            aria-describedby={error ? 'ac-reg-error' : undefined}
          />
        </div>

        {status !== 'success' ? (
          <button
            type="button"
            className="ac-btn ac-btn--secondary"
            onClick={onLookup}
            disabled={status === 'loading' || registration.trim().length === 0}
          >
            {status === 'loading' ? (
              <>
                <span className="ac-spinner" aria-hidden="true" />
                Looking up
              </>
            ) : (
              'Find my vehicle'
            )}
          </button>
        ) : null}
      </div>

      <div aria-live="polite">
        {vehicle ? (
          <div className="ac-vehicle">
            <CheckIcon size={20} />
            <span className="ac-vehicle__name">{vehicleSummary(vehicle)}</span>
          </div>
        ) : null}

        {error ? (
          <div className="ac-notice ac-notice--error" role="alert" id="ac-reg-error">
            <AlertIcon size={18} />
            <span>{error}</span>
          </div>
        ) : null}
      </div>

      {!lookupRequired && !showManual && status !== 'success' ? (
        <div className="ac-stack ac-stack--tight">
          <button
            type="button"
            className="ac-btn ac-btn--ghost"
            onClick={() => setShowManualEntry(true)}
          >
            Enter my vehicle details manually
          </button>
          {onSkip ? (
            <button type="button" className="ac-btn ac-btn--ghost" onClick={onSkip}>
              I don&apos;t know my registration, skip for now
            </button>
          ) : null}
        </div>
      ) : null}

      {showManual ? (
        <div className="ac-stack ac-stack--tight">
          <p className="ac-hint">We&apos;ll confirm the exact details when you arrive.</p>
          <div className="ac-field">
            <label className="ac-label" htmlFor="ac-make">
              Make
            </label>
            <input
              id="ac-make"
              className="ac-input"
              value={manualVehicle?.make ?? ''}
              placeholder="Ford"
              onChange={(event) =>
                onManualVehicleChange({
                  make: event.target.value,
                  model: manualVehicle?.model ?? '',
                })
              }
            />
          </div>
          <div className="ac-field">
            <label className="ac-label" htmlFor="ac-model">
              Model
            </label>
            <input
              id="ac-model"
              className="ac-input"
              value={manualVehicle?.model ?? ''}
              placeholder="Focus"
              onChange={(event) =>
                onManualVehicleChange({
                  make: manualVehicle?.make ?? '',
                  model: event.target.value,
                })
              }
            />
          </div>
        </div>
      ) : null}
    </div>
  )
}
